// A/B tests. Shared by the server and the browser bundle, so it stays free of Node and DOM APIs.
//
// A phone's group comes from its own random id (the `did` in src/client/track.ts), so the same phone
// always lands in the same group without storing anything: the landing page, the payer's screens
// and the server all work it out the same way. Friends at a dinner see the payer's group, never
// their own, because the group is saved on the split when it's made.
//
// The first test is the app fee, paid by the host when they lock the split in. What changes is
// who pays and what friends add:
//   A  payer pays           friends add nothing
//   B  table thanks payer   no fee; each friend adds $1 to the payer (the existing "thanks")
//   C  table covers it      the host pays it, friends share it back, sent to the payer with their share
import type { Cents } from './types.ts';
import { pricingEnabled } from './pricing.ts';

export type FeeGroup = 'A' | 'B' | 'C';
export const FEE_GROUPS: readonly FeeGroup[] = ['A', 'B', 'C'];

export const FEE_GROUP_NAMES: Record<FeeGroup, string> = {
  A: 'Payer pays',
  B: 'Table thanks the payer',
  C: 'Table covers the fee',
};

/** Dinners smaller than this are free and stay out of the test: two people don't need the app. */
export const FEE_MIN_TABLE = 3;

/**
 * Whether the host's tip step offers the "$1 thanks from each friend" switch: only with pricing on,
 * at a table of 3 or more, and only when no fee line takes its place.
 */
export const thanksSwitchShown = (pricing: boolean, partySize: number, feeNote: string): boolean =>
  pricing && !feeNote && partySize >= FEE_MIN_TABLE;

/** What the server tells phones about the fee test. */
export interface FeeConfig {
  on: boolean;
  /** What the payer's card is charged per split. */
  cents: Cents;
  /** Groups still in the test. One group means everyone gets it (a winner was picked). */
  groups: FeeGroup[];
  /** Payers each group needs before the Experiments page calls a result. */
  minPayers: number;
}

export const FEE_OFF: FeeConfig = { on: false, cents: 149, groups: ['A', 'C'], minPayers: 160 };

/**
 * From Railway Variables. FEE_TEST=on starts it, but only with PRICING_ENABLED=on too. FEE_GROUPS=A,C
 * picks the groups (A against C runs first). FEE_WINNER=C ends it and gives everyone C. FEE_CENTS and FEE_MIN_PAYERS tune it.
 */
export function feeConfigFrom(env: Record<string, string | undefined>): FeeConfig {
  const pick = (v: string | undefined) => (v || '').toUpperCase().split(/[\s,]+/).filter((g): g is FeeGroup => (FEE_GROUPS as string[]).includes(g));
  const winner = pick(env.FEE_WINNER)[0];
  const groups = winner ? [winner] : [...new Set(pick(env.FEE_GROUPS))];
  const cents = Math.round(Number(env.FEE_CENTS));
  const min = Math.round(Number(env.FEE_MIN_PAYERS));
  return {
    // FEE_TEST picks fee groups only while pricing is on (src/pricing.ts). Off: nobody gets a group.
    on: /^(on|1|true|yes)$/i.test(env.FEE_TEST || '') && pricingEnabled(env),
    cents: cents >= 50 && cents <= 1000 ? cents : FEE_OFF.cents,
    groups: groups.length ? groups : FEE_OFF.groups,
    minPayers: min > 0 ? min : FEE_OFF.minPayers,
  };
}

/** A number in [0, 1) that a phone id always maps to, per test. FNV-1a, so it's the same everywhere. */
export function bucket(did: string, test: string): number {
  let h = 0x811c9dc5;
  for (const ch of test + ':' + did) {
    h ^= ch.charCodeAt(0);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h / 0x1_0000_0000;
}

/** This phone's fee group, or null when the test is off. */
export function feeGroupFor(cfg: Pick<FeeConfig, 'on' | 'groups'>, did: string): FeeGroup | null {
  if (!cfg.on || !cfg.groups.length) return null;
  return cfg.groups[Math.floor(bucket(did, 'fee') * cfg.groups.length)];
}

/** The fee as it's saved on a split: its group and what the payer's card is charged. */
export interface SplitFee {
  group: FeeGroup;
  cents: Cents;
  /** Set by a creator code with a fixed price, not by the test: left out of the results. */
  fixed?: boolean;
  /**
   * Group C: the friends on the split when the host locked it in. The fee is shared across them and
   * frozen there: a friend who joins later pays no part of it.
   */
  friendIds?: string[];
  /**
   * Group C: each friend's part, frozen at lock-in. The same number they were shown before it. 0 when
   * the split is free for the host (first split or referral credit), set as soon as that's decided.
   */
  shareCents?: Cents;
}

/**
 * Group C: each friend's part of the fee. The fee is split across the friends at the table (the
 * table size the payer set, or everyone who joined if more did, minus the payer), rounded up:
 * share = ceil(fee / (table - 1)), so the host never covers an odd cent ("Costs you $0") and the
 * friends between them pay at most a few cents over the fee (a table of 5: 4 x 38¢ = $1.52 for
 * $1.49). Changed from rounding down on Oct 19, 2026. This one number is what a friend is told
 * before lock-in, what's in their total after it, and what the Experiments page counts. At lock-in
 * it's frozen (`shareCents`) for the friends there then (`friendIds`); a friend who joins later pays
 * none of it, and nobody's part changes. A split locked before the change keeps its frozen share.
 */
export function friendFeeCents(fee: SplitFee | undefined, partySize: number, peopleCount: number): Cents {
  if (!fee || fee.group !== 'C') return 0;
  if (fee.friendIds && !fee.friendIds.length) return 0;
  if (fee.shareCents !== undefined) return fee.shareCents;
  // Locked before shareCents was saved: the table as it was at lock-in.
  const people = fee.friendIds ? Math.max(partySize || 0, fee.friendIds.length + 1, 2) : Math.max(partySize || 0, peopleCount, 2);
  return Math.ceil(fee.cents / (people - 1));
}

/** What we keep from a charge after Stripe's standard US card fee (2.9% + 30¢). */
export const netAfterStripe = (cents: Cents): Cents => cents - Math.round(cents * 0.029) - 30;

/**
 * The price line each group sees on landing pages and the payer's tip step. These screens come before
 * we know who's paying, so they never promise a split with no fee: whether this one is waived (a
 * referred host's first split, a code's perk, a credit) is decided later and shown on the host's board
 * ("no fee" on the button). QATE78: a host on their third split read "No fee on the first split" and paid $1.49.
 */
export function feeLine(group: FeeGroup, cents: Cents, perFriend?: Cents): string {
  const price = (c: Cents) => '$' + (c / 100).toFixed(2);
  if (group === 'A') return `No subscription. ${price(cents)} app fee per split.`;
  if (group === 'B') return 'No subscription. Pay per split. No fee for you. Each friend adds $1 thanks to you.';
  return `No subscription. Pay per split. No fee for whoever pays. Friends cover the ${price(cents)} app fee${perFriend ? `, ${perFriend}¢ each` : ', a few cents each'}.`;
}

/** Where payers came from, to cut the Experiments page by. */
export const EXPERIMENT_SOURCES = ['creator', 'referral', 'ads', 'table', 'sms', 'direct'] as const;
export type ExperimentSource = (typeof EXPERIMENT_SOURCES)[number];

/** A share with what it's out of, so a page can say "12 of 40" and not just "30%". */
export interface Rate { num: number; den: number }

// ---------- the group-text button ----------
// Split by table: the split's random abSeed (or its code, for splits made before abSeed) picks the arm, so every screen of one dinner agrees and the
// arm is saved on the split when it's made. "share": "Send to the group text" is the main button.
// "qr": the QR code stays the main way in, as before.
export type GroupShareArm = 'share' | 'qr';
export const GROUP_SHARE_ARMS: readonly GroupShareArm[] = ['share', 'qr'];
export const groupShareArmFor = (seed: string): GroupShareArm => (bucket(seed, 'group_share') < 0.5 ? 'share' : 'qr');
